import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { useSetAutoChargeMutation } from '../hooks/useSetAutoChargeMutation';
import { Customer } from '../types';
import { AutoPayIcon } from '../../assets/icons';
import { useIsTabletOrLarger } from '../../responsive/hooks/hooks';
import { v4 as uuidv4 } from 'uuid';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';
import { StripePaymentMethodDto } from '../dto';

const source = 'billing';

const autoChargeTooltipText =
  'When enabling automatic payments you agree for Growegy to charge you at the end of the billing period if the invoice is not paid based on Terms and Conditions';

const AutoChargeToggle = ({
  isChecked,
  onChange,
  tooltipId,
}: {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  tooltipId: string;
}) => (
  <div className="switch-input-container flex-row w-100" style={{ padding: '3px 6px' }}>
    <input
      type="checkbox"
      className="custom-control-input"
      id="billing__auto-charge-enabled"
      data-test="billing__auto-charge-enabled-input"
      onChange={(e) => onChange(e.target.checked)}
      checked={isChecked}
    />
    <label
      className="custom-control-label calendar-display-settings__check-label billing__sms-toggle"
      htmlFor="billing__auto-charge-enabled"
      data-test="billing__auto-charge-enabled-toggle"
      data-tooltip-id={tooltipId}
      data-tooltip-place="bottom"
    />
  </div>
);

const AutoChargeCells = ({
  isChecked,
  onChange,
  tooltipId,
}: {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  tooltipId: string;
}) => (
  <>
    <div
      className="billing-table__column billing-table__column--size8 text--truncated"
      data-test="billing-auto-charge__description-cell"
    >
      <div className="d-flex align-items-center justify-content-start">
        <div className="billing__icon">
          <AutoPayIcon className="d-block" />
        </div>
        <div className="growegy-label14--moderrat flex-grow-1">Enable automatic payments</div>
      </div>
    </div>
    <div className="billing-table__column billing-table__column--size4" />
    <div className="billing-table__column billing-table__column--size3 flex-row">
      <AutoChargeToggle isChecked={isChecked} onChange={onChange} tooltipId={tooltipId} />
    </div>
  </>
);

const AutoChargeCellsNarrow = ({
  isChecked,
  onChange,
  tooltipId,
}: {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  tooltipId: string;
}) => (
  <>
    <div
      className="billing-table__column text--truncated flex-grow-1"
      data-test="billing-auto-charge__description-cell"
    >
      <div className="d-flex align-items-center justify-content-start">
        <div className="billing__icon">
          <AutoPayIcon className="d-block" />
        </div>
        <div className="growegy-label14--moderrat flex-grow-1">Enable automatic payments</div>
      </div>
    </div>
    <div>
      <AutoChargeToggle isChecked={isChecked} onChange={onChange} tooltipId={tooltipId} />
    </div>
  </>
);

export const AutoChargeSwitch = ({
  customer,
  paymentMethods,
}: {
  customer: Customer;
  paymentMethods: StripePaymentMethodDto[];
}) => {
  const [modalKey, setModalKey] = useState<string | null>(null);
  const { mutate: setAutoCharge } = useSetAutoChargeMutation(source, () => {
    if (paymentMethods.length === 0) {
      setModalKey(uuidv4());
    }
  });
  const isChecked = customer.autoChargeEnabled ?? false;
  const isTabletOrLarger = useIsTabletOrLarger();
  const [tooltipId] = useState(`tooltip-id_${uuidv4()}`);

  const handleChange = (checked: boolean) => {
    setAutoCharge({ enabled: checked });
  };

  return (
    <>
      <div className="billing-flex-table__row" data-test="billing-auto-charge__row">
        {isTabletOrLarger ? (
          <AutoChargeCells isChecked={isChecked} onChange={handleChange} tooltipId={tooltipId} />
        ) : (
          <AutoChargeCellsNarrow
            isChecked={isChecked}
            onChange={handleChange}
            tooltipId={tooltipId}
          />
        )}
        <Tooltip
          id={tooltipId}
          className="info-button__tooltip"
          noArrow={true}
          style={{ width: 256 }}
          place="bottom"
        >
          <div className="info-button__tooltip-content">{autoChargeTooltipText}</div>
        </Tooltip>
      </div>
      {modalKey && <AddPaymentMethodModal key={modalKey} />}
    </>
  );
};
